import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useState, useCallback } from 'react'
import { useEntityAccounts, type EntityAccount } from '../../features/entity/hooks/useEntityDetail'
import { useEnrichment, ENRICHMENT_SOURCES } from '../../features/entity/hooks/useEnrichment'
import { LocationMap } from '../../features/entity/components/LocationMap'
import { Avatar } from '../../components/Avatar'
import { Tabs, TabsList, TabsTrigger, TabsContentKeepAlive } from '../../components/Tabs'
import { Skeleton } from '../../components/Skeleton'
import { Spinner } from '../../components/Spinner'
import { Button } from '../../components/Button'
import { api } from '../../lib/api'
import { queryClient } from '../../lib/query-client'

/* ───── Types ───── */

interface ProfileImage {
  url: string
  platform: string
}

interface OsintSource {
  source_id: string
  source_name: string
  contributed: boolean
  last_queried: string | null
  data_types: string[]
}

interface ProfileResponse {
  profile: {
    entity_id: string
    name: string
    phone_numbers: string[]
    emails: string[]
    social_media: SocialMediaAccount[]
    linked_identities: LinkedIdentity[]
    aliases: string[]
    country: string
    language: string
    risk_score: number
    previous_risk_score?: number
    confidence_score: number
    summary: string
    first_seen: string
    last_seen: string
    platform: string
    profile_image_url: string
    profile_images?: ProfileImage[]
    osint_sources?: OsintSource[]
  }
}

interface SocialMediaAccount {
  platform: string
  username: string
  profile_url: string
  verified: boolean
  last_active: string
  confidence?: number
}

interface LinkedIdentity {
  source: string
  identifier: string
  confidence: number
  linked_at: string
}

/* ───── Query Keys ───── */

const profilerQueryKey = (id: string) => ['profiler', id] as const

/* ───── Route Definition ───── */

export const Route = createFileRoute('/_authenticated/profiler/$id')({
  component: ProfilerPage,
  loader: ({ params }) => {
    void queryClient.ensureQueryData({
      queryKey: profilerQueryKey(params.id),
      queryFn: ({ signal }) =>
        api<ProfileResponse>(`/v1/entities/${params.id}/profile`, { signal }),
      staleTime: 2 * 60 * 1000,
    })
  },
})

/* ───── Hook ───── */

function useProfiler(id: string) {
  const query = useQuery({
    queryKey: profilerQueryKey(id),
    queryFn: ({ signal }) =>
      api<ProfileResponse>(`/v1/entities/${id}/profile`, { signal }),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  })

  return {
    profile: query.data?.profile ?? null,
    isLoading: query.isLoading,
    error: query.error,
  }
}

/* ───── Image Carousel ───── */

function ImageCarousel({ images, name }: { images: ProfileImage[]; name: string }) {
  const [currentIndex, setCurrentIndex] = useState(0)

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }, [images.length])

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }, [images.length])

  if (images.length === 0) return null

  const current = images[currentIndex]!

  return (
    <div className="relative group" data-testid="image-carousel">
      <div className="relative mx-auto h-24 w-24 rounded-full overflow-hidden border-2 border-[var(--color-border)]">
        <img
          src={current.url}
          alt={`${name} - ${current.platform}`}
          className="h-full w-full object-cover"
        />
      </div>
      {/* Source label */}
      <div className="mt-1 text-center">
        <span className="inline-flex items-center rounded-full bg-[var(--color-accent)]/15 px-2 py-0.5 text-[10px] font-medium text-[var(--color-accent)]">
          {current.platform}
        </span>
        <span className="ml-1.5 text-[10px] text-[var(--color-text-muted)]">
          {currentIndex + 1} / {images.length}
        </span>
      </div>
      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={goPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 h-6 w-6 rounded-full bg-[var(--color-surface-elevated)] border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] shadow-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            aria-label="Previous image"
            data-testid="carousel-prev"
          >
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 h-6 w-6 rounded-full bg-[var(--color-surface-elevated)] border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] shadow-sm opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            aria-label="Next image"
            data-testid="carousel-next"
          >
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </>
      )}
    </div>
  )
}

/* ───── Risk Score Trend Arrow ───── */

function RiskScoreTrend({ current, previous }: { current: number; previous?: number }) {
  if (previous == null) {
    return (
      <span className="text-lg font-bold text-[var(--color-text-heading)]" data-testid="risk-score-value">
        {Math.round(current)}
      </span>
    )
  }

  const diff = current - previous
  const isUp = diff > 0
  const isDown = diff < 0

  return (
    <span className="inline-flex items-center gap-1" data-testid="risk-score-trend">
      <span className="text-lg font-bold text-[var(--color-text-heading)]">
        {Math.round(current)}
      </span>
      {isUp && (
        <svg className="h-4 w-4 text-[var(--color-danger)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} data-testid="risk-trend-up">
          <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
      )}
      {isDown && (
        <svg className="h-4 w-4 text-[var(--color-success)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} data-testid="risk-trend-down">
          <path d="M12 5v14M19 12l-7 7-7-7" />
        </svg>
      )}
      {!isUp && !isDown && (
        <svg className="h-4 w-4 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} data-testid="risk-trend-flat">
          <path d="M5 12h14" />
        </svg>
      )}
    </span>
  )
}

/* ───── Confidence Badge for Social Media Cards ───── */

function ConfidenceBadge({ confidence }: { confidence?: number }) {
  if (confidence == null) return null

  let label: string
  let colorClass: string

  if (confidence >= 0.8) {
    label = 'High'
    colorClass = 'bg-green-500/15 text-green-400'
  } else if (confidence >= 0.5) {
    label = 'Medium'
    colorClass = 'bg-yellow-500/15 text-yellow-400'
  } else {
    label = 'Low'
    colorClass = 'bg-red-500/15 text-red-400'
  }

  return (
    <span
      className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${colorClass}`}
      data-testid="confidence-badge"
    >
      {Math.round(confidence * 100)}% {label}
    </span>
  )
}

/* ───── Refresh Enrichment Button ───── */

function RefreshEnrichmentButton({ entityId }: { entityId: string }) {
  const { startEnrichment, isStarting, taskStatuses, isPolling } = useEnrichment(entityId)

  const handleRefresh = useCallback(() => {
    startEnrichment(ENRICHMENT_SOURCES.map((s) => s.id))
  }, [startEnrichment])

  return (
    <div data-testid="refresh-enrichment">
      <Button
        variant="ghost"
        size="sm"
        onClick={handleRefresh}
        disabled={isStarting || isPolling}
        data-testid="refresh-enrichment-btn"
      >
        {isStarting || isPolling ? (
          <Spinner size="sm" className="h-3.5 w-3.5 mr-1.5" />
        ) : (
          <svg className="h-3.5 w-3.5 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
            <path d="M3 3v5h5" />
            <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
            <path d="M16 16h5v5" />
          </svg>
        )}
        Refresh Enrichment
      </Button>

      {/* Per-source progress indicators */}
      {taskStatuses.length > 0 && (
        <div className="mt-2 space-y-1">
          {taskStatuses.map((task) => (
            <div key={task.task_id} className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-[var(--color-bg-tertiary)] overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    task.status === 'failed'
                      ? 'bg-[var(--color-danger)]'
                      : task.status === 'completed'
                        ? 'bg-[var(--color-success)]'
                        : 'bg-[var(--color-accent)]'
                  }`}
                  style={{ width: `${task.progress}%` }}
                />
              </div>
              <span className="text-[10px] text-[var(--color-text-muted)] w-16 text-right">
                {task.status === 'completed' ? 'Done' : task.status === 'failed' ? 'Failed' : `${task.progress}%`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ───── OSINT Data Sources Indicator ───── */

const KNOWN_OSINT_SOURCES = [
  { id: 'callerapi', name: 'CallerAPI', icon: 'phone' },
  { id: 'have_i_been_pwned', name: 'HIBP', icon: 'shield' },
  { id: 'social_search', name: 'Social Search', icon: 'users' },
  { id: 'whois', name: 'WHOIS', icon: 'globe' },
  { id: 'shodan', name: 'Shodan', icon: 'server' },
  { id: 'dehashed', name: 'Dehashed', icon: 'database' },
]

function OsintSourceIndicators({ sources }: { sources?: OsintSource[] }) {
  const sourceMap = new Map((sources ?? []).map((s) => [s.source_id, s]))

  return (
    <div className="mt-6" data-testid="osint-sources">
      <h3 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
        OSINT Data Sources
      </h3>
      <div className="grid grid-cols-2 gap-1.5">
        {KNOWN_OSINT_SOURCES.map((known) => {
          const source = sourceMap.get(known.id)
          const contributed = source?.contributed ?? false
          return (
            <div
              key={known.id}
              className={`
                flex items-center gap-2 rounded-md px-2 py-1.5 text-xs
                ${contributed
                  ? 'bg-[var(--color-accent)]/10 text-[var(--color-accent)]'
                  : 'bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)]'
                }
              `}
              title={
                contributed
                  ? `${known.name}: data found (last queried ${source?.last_queried ? new Date(source.last_queried).toLocaleDateString() : 'unknown'})`
                  : `${known.name}: no data`
              }
              data-testid={`osint-source-${known.id}`}
            >
              {contributed ? (
                <svg className="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              ) : (
                <svg className="h-3 w-3 shrink-0 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <circle cx="12" cy="12" r="10" /><path d="M8 12h8" />
                </svg>
              )}
              <span className="truncate font-medium">{known.name}</span>
            </div>
          )
        })}
      </div>
      {sources && sources.length > 0 && (
        <p className="mt-1.5 text-[10px] text-[var(--color-text-muted)]">
          {sources.filter((s) => s.contributed).length} of {KNOWN_OSINT_SOURCES.length} sources contributed data
        </p>
      )}
    </div>
  )
}

/* ───── PDF Export Button ───── */

function ProfilePdfExportButton({ entityId, name }: { entityId: string; name: string }) {
  const exportMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`/api/v1/entities/${entityId}/profile/export-pdf`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      })
      if (!response.ok) throw new Error('PDF export failed')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `profile-${name.replace(/\s+/g, '-').toLowerCase()}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
  })

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => exportMutation.mutate()}
      disabled={exportMutation.isPending}
      data-testid="profile-pdf-export-btn"
    >
      {exportMutation.isPending ? (
        <Spinner size="sm" className="h-3.5 w-3.5 mr-1.5" />
      ) : (
        <svg className="h-3.5 w-3.5 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="M12 18v-6M9 15l3 3 3-3" />
        </svg>
      )}
      Export PDF
    </Button>
  )
}

/* ───── Sub-components ───── */

function InfoRow({ label, value }: { label: string; value: string | undefined | null }) {
  if (!value) return null
  return (
    <div className="flex items-start gap-3 py-2 border-b border-[var(--color-border-light)] last:border-b-0">
      <span className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider w-28 shrink-0 pt-0.5">
        {label}
      </span>
      <span className="text-sm text-[var(--color-text-primary)]">{value}</span>
    </div>
  )
}

function PhoneNumbersList({ phones }: { phones: string[] }) {
  if (!phones.length) {
    return <p className="text-sm text-[var(--color-text-muted)]">No phone numbers on record</p>
  }
  return (
    <div className="space-y-2">
      {phones.map((phone, i) => (
        <div
          key={i}
          className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2"
        >
          <svg className="h-4 w-4 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          <span className="text-sm font-mono text-[var(--color-text-primary)]">{phone}</span>
        </div>
      ))}
    </div>
  )
}

function SocialMediaList({ accounts }: { accounts: SocialMediaAccount[] }) {
  if (!accounts.length) {
    return <p className="text-sm text-[var(--color-text-muted)]">No social media accounts found</p>
  }

  const platformColors: Record<string, string> = {
    telegram: 'bg-blue-500/15 text-blue-400',
    whatsapp: 'bg-green-500/15 text-green-400',
    discord: 'bg-indigo-500/15 text-indigo-400',
    signal: 'bg-sky-500/15 text-sky-400',
    default: 'bg-gray-500/15 text-gray-400',
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {accounts.map((account, i) => {
        const colorClass = platformColors[account.platform.toLowerCase()] ?? platformColors.default
        return (
          <div
            key={i}
            className="flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3"
          >
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}>
              {account.platform}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                {account.username}
              </p>
              {account.profile_url && (
                <a
                  href={account.profile_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[var(--color-accent)] hover:underline truncate block"
                >
                  View profile
                </a>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              {account.verified && (
                <svg className="h-4 w-4 text-[var(--color-success)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
              <ConfidenceBadge confidence={account.confidence} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function LinkedIdentitiesList({ identities }: { identities: LinkedIdentity[] }) {
  if (!identities.length) {
    return <p className="text-sm text-[var(--color-text-muted)]">No linked identities found</p>
  }
  return (
    <div className="space-y-2">
      {identities.map((identity, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3"
        >
          <div>
            <p className="text-sm font-medium text-[var(--color-text-primary)]">{identity.identifier}</p>
            <p className="text-xs text-[var(--color-text-muted)]">Source: {identity.source}</p>
          </div>
          <div className="text-right">
            <div className="text-xs font-medium text-[var(--color-text-secondary)]">
              {Math.round(identity.confidence * 100)}% confidence
            </div>
            {identity.linked_at && (
              <p className="text-xs text-[var(--color-text-muted)]">
                {new Date(identity.linked_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function AccountsPanel({ entityId }: { entityId: string }) {
  const { accounts, isLoading } = useEntityAccounts(entityId)

  if (isLoading) {
    return <Skeleton className="h-40 w-full rounded-lg" />
  }

  if (!accounts.length) {
    return <p className="text-sm text-[var(--color-text-muted)]">No cross-platform accounts found</p>
  }

  return (
    <div className="space-y-2">
      {accounts.map((account: EntityAccount, i: number) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3"
        >
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center rounded-full bg-[var(--color-accent)]/15 px-2 py-0.5 text-xs font-medium text-[var(--color-accent)]">
              {account.platform}
            </span>
            <div>
              <p className="text-sm font-medium text-[var(--color-text-primary)]">
                {account.display_name || account.username}
              </p>
              {account.username && (
                <p className="text-xs text-[var(--color-text-muted)]">@{account.username}</p>
              )}
            </div>
          </div>
          <span
            className={`text-xs font-medium ${
              account.enrichment_status === 'completed'
                ? 'text-[var(--color-success)]'
                : account.enrichment_status === 'failed'
                  ? 'text-[var(--color-danger)]'
                  : 'text-[var(--color-text-muted)]'
            }`}
          >
            {account.enrichment_status}
          </span>
        </div>
      ))}
    </div>
  )
}

/* ───── Page Component ───── */

function ProfilerPage() {
  const { id } = Route.useParams()
  const { profile, isLoading, error } = useProfiler(id)

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="rounded-lg border border-[var(--color-danger)]/30 bg-[var(--color-danger)]/5 p-6 text-center">
          <h2 className="text-lg font-semibold text-[var(--color-danger)]">Profile Not Found</h2>
          <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
            The requested entity profile could not be loaded.
          </p>
          <Link
            to="/entities/$id"
            params={{ id }}
            className="mt-4 inline-block text-sm text-[var(--color-accent)] hover:underline"
          >
            View entity detail instead
          </Link>
        </div>
      </div>
    )
  }

  if (isLoading || !profile) {
    return (
      <div className="flex h-full" data-testid="profiler-page-loading">
        <div className="w-80 border-r border-[var(--color-border)] bg-[var(--color-surface)] p-6">
          <Skeleton className="h-24 w-24 rounded-full mx-auto mb-4" />
          <Skeleton className="h-6 w-48 mx-auto mb-2" />
          <Skeleton className="h-4 w-32 mx-auto mb-6" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-full mb-2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full" data-testid="profiler-page">
      {/* Left Panel: Avatar & Basic Info */}
      <aside className="w-80 shrink-0 border-r border-[var(--color-border)] bg-[var(--color-surface)] overflow-y-auto">
        <div className="p-6">
          {/* Avatar / Image Carousel */}
          <div className="flex flex-col items-center mb-6">
            {profile.profile_images && profile.profile_images.length > 0 ? (
              <ImageCarousel images={profile.profile_images} name={profile.name} />
            ) : (
              <Avatar
                src={profile.profile_image_url}
                name={profile.name}
                size="xl"
                className="mb-3"
              />
            )}
            <h1 className="mt-2 text-lg font-bold text-[var(--color-text-heading)] text-center">
              {profile.name}
            </h1>
            {profile.platform && (
              <span className="mt-1 inline-flex items-center rounded-full bg-[var(--color-accent)]/15 px-2.5 py-0.5 text-xs font-medium text-[var(--color-accent)]">
                {profile.platform}
              </span>
            )}
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-center">
              <div className="flex items-center justify-center">
                {profile.risk_score != null ? (
                  <RiskScoreTrend current={profile.risk_score} previous={profile.previous_risk_score} />
                ) : (
                  <span className="text-lg font-bold text-[var(--color-text-heading)]">--</span>
                )}
              </div>
              <p className="text-xs text-[var(--color-text-muted)]">Risk Score</p>
            </div>
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-center">
              <p className="text-lg font-bold text-[var(--color-text-heading)]">
                {profile.confidence_score != null ? Math.round(profile.confidence_score * 100) + '%' : '--'}
              </p>
              <p className="text-xs text-[var(--color-text-muted)]">Confidence</p>
            </div>
          </div>

          {/* Basic Details */}
          <div className="space-y-0">
            <InfoRow label="Country" value={profile.country} />
            <InfoRow label="Language" value={profile.language} />
            <InfoRow label="First Seen" value={profile.first_seen ? new Date(profile.first_seen).toLocaleDateString() : null} />
            <InfoRow label="Last Seen" value={profile.last_seen ? new Date(profile.last_seen).toLocaleDateString() : null} />
          </div>

          {/* Aliases */}
          {profile.aliases && profile.aliases.length > 0 && (
            <div className="mt-6">
              <h3 className="text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-2">
                Known Aliases
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {profile.aliases.map((alias, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center rounded-full bg-[var(--color-bg-tertiary)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)]"
                  >
                    {alias}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* OSINT Data Source Indicators */}
          <OsintSourceIndicators sources={profile.osint_sources} />

          {/* Refresh Enrichment */}
          <div className="mt-6 pt-4 border-t border-[var(--color-border-light)]">
            <RefreshEnrichmentButton entityId={id} />
          </div>

          {/* PDF Export */}
          <div className="mt-3">
            <ProfilePdfExportButton entityId={id} name={profile.name} />
          </div>

          {/* Link to full entity detail */}
          <div className="mt-4 pt-4 border-t border-[var(--color-border-light)]">
            <Link
              to="/entities/$id"
              params={{ id }}
              className="flex items-center gap-2 text-sm text-[var(--color-accent)] hover:underline"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
              View full entity detail
            </Link>
          </div>
        </div>
      </aside>

      {/* Right Panel: Tabbed Detail */}
      <main className="flex-1 overflow-hidden">
        <Tabs defaultValue="social-media" className="h-full flex flex-col">
          <div className="border-b border-[var(--color-border)] bg-[var(--color-surface)] px-6">
            <TabsList>
              <TabsTrigger value="social-media">Social Media</TabsTrigger>
              <TabsTrigger value="phone-numbers">Phone Numbers</TabsTrigger>
              <TabsTrigger value="linked-identities">Linked Identities</TabsTrigger>
              <TabsTrigger value="accounts">Accounts</TabsTrigger>
              <TabsTrigger value="locations">Locations</TabsTrigger>
              <TabsTrigger value="summary">Summary</TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <TabsContentKeepAlive value="social-media">
              <h2 className="text-base font-semibold text-[var(--color-text-heading)] mb-4">
                Social Media Accounts
              </h2>
              <SocialMediaList accounts={profile.social_media ?? []} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="phone-numbers">
              <h2 className="text-base font-semibold text-[var(--color-text-heading)] mb-4">
                Phone Numbers
              </h2>
              <PhoneNumbersList phones={profile.phone_numbers ?? []} />

              {profile.emails && profile.emails.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-[var(--color-text-heading)] mb-3">
                    Email Addresses
                  </h3>
                  <div className="space-y-2">
                    {profile.emails.map((email, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] px-3 py-2"
                      >
                        <svg className="h-4 w-4 text-[var(--color-text-muted)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <rect width="20" height="16" x="2" y="4" rx="2" />
                          <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                        </svg>
                        <span className="text-sm text-[var(--color-text-primary)]">{email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="linked-identities">
              <h2 className="text-base font-semibold text-[var(--color-text-heading)] mb-4">
                Linked Identities
              </h2>
              <LinkedIdentitiesList identities={profile.linked_identities ?? []} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="accounts">
              <h2 className="text-base font-semibold text-[var(--color-text-heading)] mb-4">
                Cross-Platform Accounts
              </h2>
              <AccountsPanel entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="locations">
              <h2 className="text-base font-semibold text-[var(--color-text-heading)] mb-4">
                Known Locations
              </h2>
              <LocationMap entityId={id} />
            </TabsContentKeepAlive>

            <TabsContentKeepAlive value="summary">
              <h2 className="text-base font-semibold text-[var(--color-text-heading)] mb-4">
                Profile Summary
              </h2>
              {profile.summary ? (
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                  <p className="text-sm text-[var(--color-text-primary)] leading-relaxed whitespace-pre-wrap">
                    {profile.summary}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-[var(--color-text-muted)]">
                  No AI-generated summary available yet. Trigger enrichment from the entity detail page.
                </p>
              )}
            </TabsContentKeepAlive>
          </div>
        </Tabs>
      </main>
    </div>
  )
}
